<template>
  <!-- prettier-ignore -->
  <span
    class="price"
    :class="{'price--loading': !hasPrice}"
  >
    <template v-if="isValidPrice($props.price)">
      <span
        v-if="$props.prefix"
        class="price__prefix"
        :class="$props.prefixClass"
        v-text="$props.prefix"
      />
      <span
        class="price__value"
        :class="$props.priceClass"
      >
        <span ref="priceValueRef" />
        <slot></slot>
      </span>
      <span
        v-if="$props.suffix"
        class="price__suffix"
        :class="$props.suffixClass"
        v-text="$props.suffix"
      />
      <span
        v-if="$props.additional"
        class="price__additional"
        v-text="$props.additional"
      />
    </template>
    <template v-else-if="shouldShowUnavailable">
      <span
        class="price__unavailable"
        v-text="$t('product.unavailable')"
      />
    </template>
  </span>
</template>

<script setup>
import { computed, ref, watchEffect, nextTick, onMounted } from 'vue'

import { CountUp } from 'countup.js'

// INIT
const props = defineProps({
  additional: {
    type: String,
    default: null,
  },
  decimals: {
    type: Number,
    default: 2,
  },
  prefix: {
    type: String,
    default: '',
  },
  prefixClass: {
    type: String,
    default: 'body-2',
  },
  price: {
    type: [Number, String],
    default: null,
  },
  priceClass: {
    type: String,
    default: '',
  },
  showZeroValue: {
    type: Boolean,
    default: false,
  },
  suffix: {
    type: String,
    default: null,
  },
  suffixClass: {
    type: String,
    default: 'body-2',
  },
})

// DATA
let installed = ref(false)
const priceValueRef = ref(null)
const selector = ref(null)
const isMounted = ref(false)
const isInitialLoad = ref(true)

// COMPUTED
const hasPrice = computed(() => {
  return isValidPrice(props.price)
})

const shouldShowUnavailable = computed(() => {
  // During initial load or when price is null, don't show "Not available"
  if (isInitialLoad.value || props.price === null) {
    return false
  }
  return !hasPrice.value
})

// METHODS
async function install() {
  if (!isMounted.value || !priceValueRef.value || installed.value) return
  
  await nextTick() // Ensure DOM is fully updated
  
  if (!priceValueRef.value) return // Double check after nextTick
  
  const el = priceValueRef.value
  const price = isValidPrice(props.price) ? props.price / 100 : 0
  const options = {
    decimalPlaces: props.decimals,
    duration: 0.5,
  }

  // Clean up any existing CountUp instance
  if (selector.value) {
    selector.value = null
  }

  selector.value = new CountUp(el, price, options)
  if (selector.value.error) {
    console.error('CountUp error:', selector.value.error)
    // Fallback: directly set the text content
    el.textContent = price.toFixed(props.decimals)
    return
  }
  
  selector.value.start()
  installed.value = true
}

function isValidPrice(value) {
  return value !== undefined && value !== null && !isNaN(Number(value))
}

async function updateSelector(value) {
  if (!installed.value || !selector.value) {
    // If not installed or no selector, reinstall
    await install()
    return
  }
  
  const price = isValidPrice(value) ? value / 100 : 0
  selector.value.update(price)
}

function cleanup() {
  installed.value = false
  selector.value = null
}

// LIFECYCLE HOOKS
onMounted(() => {
  isMounted.value = true
  nextTick(() => {
    isInitialLoad.value = false
  })
})

watchEffect(async () => {
  if (isValidPrice(props.price) && isMounted.value) {
    if (!installed.value || !selector.value) {
      await install()
    } else {
      await updateSelector(props.price)
    }
  } else {
    cleanup()
  }
})
</script>

<style scoped>
.price {
  display: inline-block;
  white-space: nowrap;

  &--loading {
    .price__value {
      opacity: 0.1;
    }
  }

  &__value {
    transition: opacity 0.5s ease-in-out;
  }

  &__prefix {
    padding-right: var(--fixed-spacing-fix-02);
  }

  &__suffix {
    padding-left: var(--fixed-spacing-fix-02);
  }

  &__prefix,
  &__suffix {
    display: inline-block;
  }

  &__additional {
    display: block;
  }

  &__unavailable {
    color: var(--c-secondary-neutral-3);
  }
}
</style>
